import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { Helmet } from 'react-helmet-async';
import LogoUrlFull from '../assets/images/logo-full.png';
const LOGO_URL = LogoUrlFull;

const navLinks = [
  { 
    name: 'Home', 
    path: '/',
    dropdown: [
      { name: 'Overview', path: '/' },
      { name: 'Our Services', path: '/#services' },
      { name: 'Therapeutic Areas', path: '/#expertise' },
      { name: 'Technology', path: '/#technology' },
    ]
  },
  { 
    name: 'About', 
    path: '/about',
    dropdown: [
      { name: 'Our Story', path: '/about' },
      { name: 'Mission & Values', path: '/about#mission' },
      // { name: 'Leadership Team', path: '/about#leadership' },
      { name: 'Company Milestones', path: '/about#milestones' },
    ]
  },
  { 
    name: 'Services', 
    path: '/services',
    dropdown: [
      { name: 'Clinical Trial Management', path: '/services#clinical-trial' },
      { name: 'Biostatistics & Data', path: '/services#biostatistics' },
      { name: 'Regulatory Affairs', path: '/services#regulatory' },
      { name: 'Medical Writing', path: '/services#medical-writing' },
      { name: 'Pharmacovigilance', path: '/services#pharmacovigilance' },
      { name: 'Drug Recall Management', path: '/services#drug-recall' },
    ]
  },
  { name: 'Staffing', path: '/staffing' },
  { 
    name: 'Technology', 
    path: '/technology',
    dropdown: [
      { name: 'Sclin EDC Platform', path: '/technology#sclin-edc' },
      { name: 'DocuDefender eTMF', path: '/technology#docudefender' },
      { name: 'AI Capabilities', path: '/technology#ai' },
    ]
  },
  { 
    name: 'Case Studies', 
    path: '/case-studies',
    dropdown: [
      { name: 'All Case Studies', path: '/case-studies' },
      { name: 'Clinical Trial Management', path: '/case-studies?category=Clinical Trial Management' },
      { name: 'Regulatory Affairs', path: '/case-studies?category=Regulatory Affairs' },
      { name: 'Biostatistics & Data', path: '/case-studies?category=Biostatistics & Data Management' },
      { name: 'Medical Writing', path: '/case-studies?category=Medical Writing' },
      { name: 'Pharmacovigilance', path: '/case-studies?category=Pharmacovigilance' },
    ]
  },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar = ({ onDemoClick }) => {
  const { siteSettings } = useSiteSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation(); 
  const logoUrl = siteSettings?.logo || "";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#090B17]/95 backdrop-blur-xl border-b border-[#2E3446] py-2' 
          : 'bg-transparent py-4'
      }`}
      data-testid="navbar"
    >
       <Helmet>
        <title>{siteSettings?.site_name || 'VetForce Solutions'}</title>
        <meta name="description" content={siteSettings?.meta_description || 'Veteran-owned staffing solutions specializing in life sciences, technology, and innovation.'} />
        <meta name="keywords" content={siteSettings?.meta_keywords || 'staffing, life sciences, technology, innovation, veteran-owned'} />
        <meta property="og:title" content={siteSettings?.site_name || 'VetForce Solutions'} />
        <meta property="og:description" content={siteSettings?.meta_description || 'Veteran-owned staffing solutions specializing in life sciences, technology, and innovation.'} />
        <meta property="og:image" content={logoUrl} />
        <meta property="og:url" content={`${window.location.origin}`} />
        <meta property="og:type" content="website" />
        {/* favicon */}
        <link rel="icon" href={siteSettings?.favicon || LOGO_URL} />
      </Helmet> 
      <div className="w-full px-6 sm:px-12 lg:px-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <img 
            src={LogoUrlFull}
            alt="VetForce Solutions" 
            className="h-11 md:h-12 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <div 
              key={link.name}
              className="relative group"
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link 
                to={link.path}
                className={`nav-link flex items-center gap-1 py-2 font-medium transition-colors ${
                  location.pathname === link.path 
                    ? 'text-[#DC2F3C]' 
                    : 'text-[#E8ECF2] hover:text-[#DC2F3C]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
                {link.dropdown && <ChevronDown className="w-3.5 h-3.5" />}
              </Link>
              
              {/* Dropdown - with padding-top to bridge gap */}
              {link.dropdown && activeDropdown === link.name && (
                <div className="absolute top-full left-0 pt-2">
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="w-64 bg-[#1B1F2C] rounded-xl overflow-hidden shadow-2xl border border-[#2E3446]"
                  >
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-3 text-sm text-[#E8ECF2] hover:text-[#DC2F3C] hover:bg-[#090B17] transition-all border-b border-[#2E3446] last:border-0"
                        data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                  </motion.div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="hidden lg:block">
          <button 
            onClick={onDemoClick}
            className="btn-gradient px-6 py-2.5 text-sm"
            data-testid="request-demo-btn"
          >
            Request Demo
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden text-[#C9C9CD] p-2"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mt-2 mx-4 bg-[#1B1F2C] rounded-xl overflow-hidden border border-[#2E3446]"
          >
            <div className="p-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link 
                    to={link.path}
                    className="block py-3 px-4 text-sm text-[#C9C9CD] hover:text-[#8F262B] hover:bg-[#090B17] rounded-lg transition-all"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-[#2E3446] pl-4">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block py-2 text-xs text-[#9F999F] hover:text-[#8F262B] transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button 
                onClick={onDemoClick}
                className="btn-gradient w-full py-3 mt-4 text-sm"
                data-testid="mobile-request-demo-btn"
              >
                Request Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
